import React, { useMemo, useState } from 'react';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';

interface RangeSliderProps {
  label: string;
  min: number;
  max: number;
  step?: number;
  value: [number, number];
  onChange: (value: [number, number]) => void;
  formatValue: (value: number, isMax?: boolean) => string;
  parseValue: (value: string) => number;
}

export const RangeSlider: React.FC<RangeSliderProps> = ({
  label,
  min,
  max,
  step = 1,
  value,
  onChange,
  formatValue,
  parseValue,
}) => {
  const [draggingHandle, setDraggingHandle] = useState<number | null>(null);
  const [inputValues, setInputValues] = useState({
    min: formatValue(value[0]),
    max: formatValue(value[1], true),
  });

  const sliderStyles = useMemo(
    () => ({
      trackStyle: [
        {
          backgroundColor: 'var(--color-mp-border)',
          height: '4px',
          borderRadius: '0px',
        },
      ],
      railStyle: {
        backgroundColor: 'var(--color-mp-surface)',
        height: 4,
        borderRadius: '0px',
      },
      handleStyle: [
        {
          borderColor: 'var(--color-mp-border)',
          backgroundColor: 'var(--color-mp-text)',
          borderWidth: 1,
          width: 12,
          height: 12,
          marginTop: -4,
          marginLeft: -6,
          opacity: 1,
          boxShadow: 'none',
          borderRadius: '0px',
        },
        {
          borderColor: 'var(--color-mp-border)',
          backgroundColor: 'var(--color-mp-text)',
          borderWidth: 1,
          width: 12,
          height: 12,
          marginTop: -4,
          marginLeft: -6,
          opacity: 1,
          boxShadow: 'none',
          borderRadius: '0px',
        },
      ].map((style, index) => ({
        ...style,
        borderColor: draggingHandle === index ? 'var(--color-primary)' : style.borderColor,
      })),
    }),
    [draggingHandle]
  );

  const handleInputChange = (type: 'min' | 'max', inputValue: string) => {
    setInputValues((prev) => ({ ...prev, [type]: inputValue }));
  };

  const handleInputBlur = (type: 'min' | 'max') => {
    const parsed = parseValue(inputValues[type]);
    const newValue =
      type === 'min'
        ? ([Math.max(min, Math.min(parsed, value[1])), value[1]] as [number, number])
        : ([value[0], Math.min(max, Math.max(parsed, value[0]))] as [number, number]);

    onChange(newValue);
    setInputValues({
      min: formatValue(newValue[0]),
      max: formatValue(newValue[1], true),
    });
  };

  return (
    <div className="group/range flex flex-col">
      <div className="flex justify-between items-center border-t border-mp-border pt-3 pb-1">
        <h3 className="font-display text-xs uppercase tracking-widest text-mp-muted">{label}</h3>
        {(value[0] !== min || value[1] !== max) && (
          <span className="font-mono text-xs text-mp-muted">
            {formatValue(value[0])} - {formatValue(value[1], true)}
          </span>
        )}
      </div>
      <div className="max-h-0 overflow-hidden group-hover/range:max-h-28 focus-within:max-h-28 transition-all duration-200 ease-in-out">
        <div className="pt-2 px-1">
          <Slider
            range
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(val) => onChange(val as [number, number])}
            onBeforeChange={() => setDraggingHandle(0)}
            onAfterChange={() => setDraggingHandle(null)}
            trackStyle={sliderStyles.trackStyle}
            railStyle={sliderStyles.railStyle}
            handleStyle={sliderStyles.handleStyle}
          />
        </div>
        <div className="flex justify-between gap-2 mt-3 pb-2">
          <input
            type="text"
            value={inputValues.min}
            onChange={(e) => handleInputChange('min', e.target.value)}
            onBlur={() => handleInputBlur('min')}
            className="w-24 bg-mp-surface border border-mp-border text-mp-text font-mono text-xs px-2 py-1 text-center focus:outline-none focus:border-primary focus:ring-0"
          />
          <span className="text-mp-muted font-mono text-xs self-center">-</span>
          <input
            type="text"
            value={inputValues.max}
            onChange={(e) => handleInputChange('max', e.target.value)}
            onBlur={() => handleInputBlur('max')}
            className="w-24 bg-mp-surface border border-mp-border text-mp-text font-mono text-xs px-2 py-1 text-center focus:outline-none focus:border-primary focus:ring-0"
          />
        </div>
      </div>
    </div>
  );
};
